import { travelerLastStepPassed } from '@parallel-fluidics/shared/models/travelers';

/**
 * Scan outcomes that appear as rows in the feed. These drive the running
 * diff and the discrepancy tiles. Values that are unusable (parse failure,
 * traveler not found, duplicate scan, final step not passed, already shipped)
 * are surfaced as an inline error instead of a feed row — they are not part
 * of a normal count.
 */
export const ScanStatus = Object.freeze({
  COUNTED: 'counted',
  NOT_EXPECTED: 'not_expected',
  OTHER_SKU: 'other_sku',
});

export const STATUS_META = Object.freeze({
  [ScanStatus.COUNTED]: { indicator: 'success', label: 'counted' },
  [ScanStatus.NOT_EXPECTED]: { indicator: 'warning', label: 'not expected' },
  [ScanStatus.OTHER_SKU]: { indicator: 'stopped', label: 'other SKU' },
});

/**
 * Classify a resolved traveler against the current session.
 *
 * @returns one of:
 *   { status }                              — a feed-row outcome (ScanStatus)
 *   { error: string }                       — inline error, no feed row
 * plus an `otherSkuLabel` for OTHER_SKU rows.
 *
 * Note: only COUNTED and NOT_EXPECTED travelers are eligible for commit, and
 * both satisfy the server callable's validation (same project/part, final step
 * passed, not shipped) so the reconcile batch will not be rejected.
 */
export default function classifyScan({
  traveler,
  session,
}) {
  const {
    productDocId, versionId, projectId, pPartId, expectedSet,
  } = session;

  const wrongPart = traveler.project !== projectId || traveler.pPart !== pPartId;
  const differentAssignment = Boolean(traveler.productDocId)
    && (traveler.productDocId !== productDocId || traveler.productVersionId !== versionId);

  if (wrongPart || differentAssignment) {
    let otherSkuLabel = 'not added';
    if (!wrongPart) {
      // Same part but assigned to another version of this product (or to the
      // product with no version recorded).
      otherSkuLabel = `version ${traveler.productVersionId || 'unassigned'} · not added`;
    } else if (traveler.project || traveler.pPart) {
      otherSkuLabel = `${[traveler.project, traveler.pPart].filter(Boolean).join('-')} · not added`;
    }
    return { status: ScanStatus.OTHER_SKU, otherSkuLabel };
  }

  if (!travelerLastStepPassed(traveler?.steps)) {
    return { error: `${traveler.id}: final step not passed — not counted.` };
  }
  if (traveler.shipped) {
    return { error: `${traveler.id}: already shipped — not counted.` };
  }

  return {
    status: expectedSet.has(traveler.id) ? ScanStatus.COUNTED : ScanStatus.NOT_EXPECTED,
  };
}
